import { useState } from 'react'
import {
  HStack,
  VStack,
  NativeSelect,
  Input,
  IconButton,
  Text,
  Box,
  Checkbox,
  CheckboxGroup,
  Wrap,
  WrapItem,
  Separator,
} from '@chakra-ui/react';
import { FiX, FiChevronDown } from 'react-icons/fi'
import { useIsMobile } from '../../hooks/useIsMobile'
import { PickerSheet } from './PickerSheet'
import type { FilterRow as FilterRowType, FilterFieldMetadata, TagOption, FilterOperator } from '../../types/contactList'
import { getOperatorsForFieldType, getValueInputType, operatorLabels, dateOperatorLabels } from './filterFieldConfig'

// pipe-delimited <-> array helpers for In/NotIn multi-select values
const splitVals = (v?: string) => (v ? v.split('|').filter(Boolean) : [])
const joinVals = (a: string[]) => a.join('|')

interface FilterRowProps {
  filter: FilterRowType
  fields: FilterFieldMetadata[]
  tags: TagOption[]
  categories: string[]
  selectedCategory: string
  onCategoryChange: (category: string) => void
  onChange: (updated: FilterRowType) => void
  onRemove: () => void
  showAndLabel: boolean
}

export function FilterRow(props: FilterRowProps) {
  const {
    filter,
    fields,
    tags,
    categories,
    selectedCategory,
    onCategoryChange,
    onChange,
    onRemove,
    showAndLabel,
  } = props
  const isMobile = useIsMobile()
  const categoryFields = fields.filter(f => f.category === selectedCategory)
  const selectedField = fields.find(f => f.fieldName === filter.fieldName)
  const fieldType = selectedField?.fieldType ?? 'text'
  const operators = getOperatorsForFieldType(fieldType)
  const valueInputType = getValueInputType(fieldType, filter.operator)

  const handleFieldChange = (fieldName: string) => {
    const field = fields.find(f => f.fieldName === fieldName)
    const newFieldType = field?.fieldType ?? 'text'
    const newOperators = getOperatorsForFieldType(newFieldType)
    onChange({
      ...filter,
      fieldName,
      operator: newOperators[0],
      value: undefined,
    })
  }

  const handleOperatorChange = (operator: string) => {
    const newInputType = getValueInputType(fieldType, operator as FilterOperator)
    onChange({
      ...filter,
      operator: operator as FilterOperator,
      // Clear value if input type changed to 'none'
      value: newInputType === 'none' ? undefined : filter.value,
    })
  }

  const getOperatorLabel = (op: FilterOperator) => {
    if (fieldType === 'date' && dateOperatorLabels[op]) return dateOperatorLabels[op]
    return operatorLabels[op]
  }

  if (!isMobile) {
  return (
    <HStack gap={3} w="100%">
      {showAndLabel && (
        <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase" minW="30px">
          AND
        </Text>
      )}

      {/* Category Select */}
      <NativeSelect.Root size="sm">
        <NativeSelect.Field
          value={selectedCategory}
          onChange={(e) => onCategoryChange(e.target.value)}
          maxW="160px">
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>

      {/* Field Select */}
      <NativeSelect.Root size="sm">
        <NativeSelect.Field
          value={filter.fieldName}
          onChange={(e) => handleFieldChange(e.target.value)}
          maxW="180px"
          placeholder="Select field...">
          {categoryFields.map(f => (
            <option key={f.fieldName} value={f.fieldName}>{f.displayLabel}</option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>

      {/* Operator Select */}
      <NativeSelect.Root size="sm">
        <NativeSelect.Field
          value={filter.operator}
          onChange={(e) => handleOperatorChange(e.target.value)}
          maxW="150px">
          {operators.map(op => (
            <option key={op} value={op}>{getOperatorLabel(op)}</option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>

      {/* Value Input */}
      {valueInputType === 'text' && (
        <Input
          size="sm"
          value={filter.value ?? ''}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          placeholder="Enter value..."
          maxW="200px"
        />
      )}

      {valueInputType === 'number' && (
        <Input
          size="sm"
          type="number"
          value={filter.value ?? ''}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          placeholder="0"
          maxW="120px"
        />
      )}

      {valueInputType === 'date' && (
        <Input
          size="sm"
          type="date"
          value={filter.value ?? ''}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          maxW="180px"
        />
      )}

      {valueInputType === 'select' && selectedField?.enumValues && (
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            value={filter.value ?? ''}
            onChange={(e) => onChange({ ...filter, value: e.target.value })}
            placeholder="Select..."
            maxW="180px">
            {selectedField.enumValues.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      )}

      {valueInputType === 'boolean' && (
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            value={filter.value ?? ''}
            onChange={(e) => onChange({ ...filter, value: e.target.value })}
            maxW="120px">
            <option value="true">Yes</option>
            <option value="false">No</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      )}

      {valueInputType === 'tag-picker' && (
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            value={filter.value ?? ''}
            onChange={(e) => onChange({ ...filter, value: e.target.value })}
            placeholder="Select tag..."
            maxW="200px">
            {tags.map(tag => (
              <option key={tag.id} value={tag.id}>
                {tag.label}{tag.tagType ? ` (${tag.tagType})` : ''}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      )}

      {valueInputType === 'multi-select' && selectedField?.enumValues && (
        <CheckboxGroup
          value={splitVals(filter.value)}
          onValueChange={(vals: string[]) => onChange({ ...filter, value: joinVals(vals as string[]) })}
        >
          <Wrap gap={2} maxW="260px">
            {selectedField.enumValues.map(v => (
              <WrapItem key={v}><Checkbox.Root size="sm" value={v}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>{v}</Checkbox.Label></Checkbox.Root></WrapItem>
            ))}
          </Wrap>
        </CheckboxGroup>
      )}

      {/* Remove button */}
      <IconButton
        aria-label="Remove filter"
        size="sm"
        variant="ghost"
        colorPalette="red"
        onClick={onRemove}><FiX /></IconButton>
    </HStack>
  );
  }

  // ---- Mobile: stacked picker buttons ----
  return <MobileFilterRow {...props} />
}

function PickerButton({ label, placeholder, onClick }: { label?: string; placeholder: string; onClick: () => void }) {
  return (
    <Box
      w="full"
      minH="44px"
      px={3}
      py={2}
      textAlign="left"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      bg="surface"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      asChild><button onClick={onClick}>
          <Text fontSize="sm" color={label ? 'gray.800' : 'gray.400'} lineClamp={1}>{label || placeholder}</Text>
          <FiChevronDown />
        </button></Box>
  );
}

function MobileFilterRow({ filter, fields, tags, selectedCategory, onCategoryChange, onChange, onRemove, showAndLabel }: FilterRowProps) {
  const selectedField = fields.find(f => f.fieldName === filter.fieldName)
  const fieldType = selectedField?.fieldType ?? 'text'
  const operators = getOperatorsForFieldType(fieldType)
  const valueInputType = getValueInputType(fieldType, filter.operator)

  const [openPicker, setOpenPicker] = useState<null | 'field' | 'operator' | 'value'>(null)

  const getOperatorLabel = (op: FilterOperator) =>
    (fieldType === 'date' && dateOperatorLabels[op]) ? dateOperatorLabels[op]! : operatorLabels[op]

  const handleFieldChange = (fieldName: string) => {
    const field = fields.find(f => f.fieldName === fieldName)
    const newOps = getOperatorsForFieldType(field?.fieldType ?? 'text')
    if (field && field.category !== selectedCategory) onCategoryChange(field.category)
    onChange({ ...filter, fieldName, operator: newOps[0], value: undefined })
  }
  const handleOperatorChange = (op: string) => {
    const newInputType = getValueInputType(fieldType, op as FilterOperator)
    onChange({ ...filter, operator: op as FilterOperator, value: newInputType === 'none' ? undefined : filter.value })
  }

  const fieldOptions = fields.map(f => ({ value: f.fieldName, label: f.displayLabel, group: f.category }))
  const operatorOptions = operators.map(op => ({ value: op, label: getOperatorLabel(op) }))
  const valueOptions =
    valueInputType === 'select' ? (selectedField?.enumValues ?? []).map(v => ({ value: v, label: v }))
    : valueInputType === 'boolean' ? [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]
    : valueInputType === 'tag-picker' ? tags.map(t => ({ value: t.id, label: `${t.label}${t.tagType ? ` (${t.tagType})` : ''}` }))
    : []
  const usesValueSheet = valueInputType === 'select' || valueInputType === 'boolean' || valueInputType === 'tag-picker'
  const isMultiSelect = valueInputType === 'multi-select' && !!selectedField?.enumValues
  const valueLabel =
    valueInputType === 'boolean' ? (filter.value === 'true' ? 'Yes' : filter.value === 'false' ? 'No' : '')
    : valueInputType === 'tag-picker' ? (tags.find(t => t.id === filter.value)?.label ?? '')
    : (filter.value ?? '')

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
      {showAndLabel && (
        <HStack mb={2}>
          <Text fontSize="2xs" fontWeight="bold" color="gray.400" textTransform="uppercase">AND</Text>
          <Separator />
        </HStack>
      )}
      <HStack justify="space-between" mb={2}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500">Condition</Text>
        <IconButton
          aria-label="Remove filter"
          size="sm"
          variant="ghost"
          colorPalette="red"
          minW="44px"
          h="44px"
          onClick={onRemove}><FiX /></IconButton>
      </HStack>
      <VStack gap={2} align="stretch">
        <PickerButton label={selectedField?.displayLabel} placeholder="Select field…" onClick={() => setOpenPicker('field')} />
        {filter.fieldName && <PickerButton label={getOperatorLabel(filter.operator)} placeholder="Condition" onClick={() => setOpenPicker('operator')} />}
        {filter.fieldName && valueInputType !== 'none' && (
          isMultiSelect ? (
            <CheckboxGroup
              value={splitVals(filter.value)}
              onValueChange={(vals: string[]) => onChange({ ...filter, value: joinVals(vals as string[]) })}
            >
              <VStack align="stretch" gap={1}>
                {selectedField!.enumValues!.map(v => (
                  <Checkbox.Root key={v} minH="44px" value={v}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>{v}</Checkbox.Label></Checkbox.Root>
                ))}
              </VStack>
            </CheckboxGroup>
          ) : usesValueSheet
            ? <PickerButton label={valueLabel || undefined} placeholder="Select value…" onClick={() => setOpenPicker('value')} />
            : (
              <Input
                size="md" minH="44px"
                type={valueInputType === 'number' ? 'number' : valueInputType === 'date' ? 'date' : 'text'}
                value={filter.value ?? ''}
                onChange={e => onChange({ ...filter, value: e.target.value })}
                placeholder="Enter value…"
              />
            )
        )}
      </VStack>

      <PickerSheet isOpen={openPicker === 'field'} onClose={() => setOpenPicker(null)} title="Select field"
        options={fieldOptions} selectedValue={filter.fieldName} onSelect={handleFieldChange} searchable />
      <PickerSheet isOpen={openPicker === 'operator'} onClose={() => setOpenPicker(null)} title="Condition"
        options={operatorOptions} selectedValue={filter.operator} onSelect={handleOperatorChange} />
      <PickerSheet isOpen={openPicker === 'value'} onClose={() => setOpenPicker(null)} title="Select value"
        options={valueOptions} selectedValue={filter.value} onSelect={(v) => onChange({ ...filter, value: v })} searchable={valueInputType === 'tag-picker'} />
    </Box>
  );
}
